import { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Link, Progress, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { githubAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { ChangesButton } from './ChangesDialog';
import { useReleasesCall } from './useReleases';

type Item = {
  slug: string;
  service: string;
  owner: string;
  base: string;
  head: string;
  status: 'esperando aprobación' | 'qa por delante';
  runUrl?: string;
  risks: string[];
};
type Release = { ticket: string; waiting: number; items: Item[] };
type Data = { jiraUrl?: string; updatedAt?: string; tickets: Release[] };

const columns: TableColumn<Item>[] = [
  { title: 'Servicio', field: 'service' },
  { title: 'Equipo', field: 'owner' },
  { title: 'Prod → nueva', field: 'head', render: i => `${i.base} → ${i.head}` },
  {
    title: 'Estado',
    field: 'status',
    render: i =>
      i.runUrl ? (
        <Link to={i.runUrl}>
          <Chip size="small" color="secondary" label="esperando aprobación" />
        </Link>
      ) : (
        <Chip size="small" label="qa por delante" />
      ),
  },
  { title: 'Riesgos', field: 'risks', render: i => (i.risks.length ? `⚠️ ${i.risks.length}` : '—') },
  { title: '', field: 'slug', sorting: false, render: i => <ChangesButton slug={i.slug} base={i.base} head={i.head} /> },
];

/**
 * Aprueba en prod, con la cuenta de GitHub de quien pulsa, los CD que esperan aprobación de un ticket. GitHub sigue
 * decidiendo quién puede aprobar (reviewers del environment prod) y la auditoría queda a nombre de esa persona.
 */
const useApprove = () => {
  const githubAuth = useApi(githubAuthApiRef);
  return async (release: Release) => {
    const token = await githubAuth.getAccessToken(['repo']);
    const gh = (path: string, init?: RequestInit) =>
      fetch(`https://api.github.com/repos/${path}`, {
        ...init,
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/vnd.github+json', 'Content-Type': 'application/json' },
      });
    const results: string[] = [];
    for (const item of release.items.filter(i => i.runUrl)) {
      const runId = item.runUrl!.match(/\/runs\/(\d+)/)?.[1];
      const pending = runId ? await gh(`${item.slug}/actions/runs/${runId}/pending_deployments`) : undefined;
      const envs = pending?.ok ? ((await pending.json()) as { environment: { id: number; name: string }; current_user_can_approve: boolean }[]) : [];
      const ids = envs.filter(e => e.current_user_can_approve).map(e => e.environment.id);
      if (!ids.length) {
        results.push(`${item.service}: sin aprobación pendiente que puedas dar`);
        continue;
      }
      const res = await gh(`${item.slug}/actions/runs/${runId}/pending_deployments`, {
        method: 'POST',
        body: JSON.stringify({ environment_ids: ids, state: 'approved', comment: `Release ${release.ticket} aprobado en bloque desde Backstage` }),
      });
      results.push(`${item.service}: ${res.ok ? 'aprobado' : `GitHub respondió ${res.status}`}`);
    }
    return results;
  };
};

const ReleaseCard = ({ release, jiraUrl, onApproved }: { release: Release; jiraUrl?: string; onApproved: () => void }) => {
  const approve = useApprove();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<string[]>();
  const isTicket = release.ticket !== 'Sin ticket';
  const run = () => {
    setBusy(true);
    approve(release)
      .then(setResults, e => setResults([String(e?.message ?? e)]))
      .finally(() => {
        setBusy(false);
        setConfirm(false);
        onApproved();
      });
  };
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography style={{ flexGrow: 1 }}>
          <b>{release.ticket}</b> · {release.items.length} msa
          {release.waiting > 0 && <Chip size="small" color="secondary" style={{ marginLeft: 8 }} label={`${release.waiting} esperando aprobación`} />}
        </Typography>
        {isTicket && jiraUrl && (
          <Link to={`${jiraUrl.replace(/\/$/, '')}/browse/${release.ticket}`} onClick={e => e.stopPropagation()} style={{ marginRight: 16 }}>
            Jira
          </Link>
        )}
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block' }}>
        <Table<Item> options={{ paging: false, search: false, toolbar: false, padding: 'dense' }} columns={columns} data={release.items} />
        {isTicket && release.waiting > 0 && (
          <Button variant="contained" color="primary" style={{ marginTop: 12 }} disabled={busy} onClick={() => setConfirm(true)}>
            Aprobar en prod los {release.waiting}
          </Button>
        )}
        {results && (
          <ul>
            {results.map(r => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        )}
        <Dialog open={confirm} onClose={() => setConfirm(false)}>
          <DialogTitle>¿Aprobar {release.ticket} en prod?</DialogTitle>
          <DialogContent>
            <Typography>
              Se aprueban con tu cuenta de GitHub los {release.waiting} despliegues a prod que esperan:{' '}
              {release.items
                .filter(i => i.runUrl)
                .map(i => `${i.service} ${i.head}`)
                .join(', ')}
              . Los msa en «qa por delante» no se tocan: aún no tienen versión para prod.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirm(false)}>Cancelar</Button>
            <Button color="primary" variant="contained" disabled={busy} onClick={run}>
              {busy ? 'Aprobando…' : 'Aprobar'}
            </Button>
          </DialogActions>
        </Dialog>
      </AccordionDetails>
    </Accordion>
  );
};

/** Release por ticket de Jira: qué msa van juntos a prod y aprobación en bloque de los que esperan. */
export const TicketReleases = () => {
  const call = useReleasesCall();
  const [data, setData] = useState<Data>();
  const [error, setError] = useState<string>();
  const [reload, setReload] = useState(0);
  useEffect(() => {
    call('/tickets').then(setData, e => setError(String(e?.message ?? e)));
  }, [call, reload]);
  if (error) return <WarningPanel title="No se pudo armar los releases por ticket" message={error} />;
  if (!data) return <Progress />;
  return (
    <>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
        Msa con algo para prod (esperando aprobación o qa por delante), agrupados por los tickets de Jira de sus commits. Un msa con varios
        tickets aparece en cada uno. La aprobación usa tu cuenta de GitHub y solo funciona si eres aprobador de prod.
      </Typography>
      {data.tickets.length === 0 && <Typography>No hay nada pendiente de llegar a prod.</Typography>}
      {data.tickets.map(r => (
        <ReleaseCard key={r.ticket} release={r} jiraUrl={data.jiraUrl} onApproved={() => setReload(n => n + 1)} />
      ))}
    </>
  );
};
